'use client';

import { c as _c } from "react/compiler-runtime";
import { closestCenter, DndContext, type DragEndEvent, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type PinnedFactiii, type SearchFactiii } from '@shared/all/domains/factiiis';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import usePinnedFactiiis from '@shared/utils/hooks/usePinnedFactiiis';
import usePinUserFactiii from '@shared/utils/hooks/usePinUserFactiii';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { GripVertical, Loader2, Search } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import Input from '../UI/Input';
import Avatar from './Avatar';
import Button from './Button';
type Props = {
  closeHandler: (factiii?: PinnedFactiii) => void;
  factiiis: PinnedFactiii[];
  /**
   * Owner view: a search to pin more, an unpin per row, and drag to reorder.
   * Off for visitors and for space banners, which manage pins elsewhere.
   */
  canEdit?: boolean;
};

// A factiii outside any space has no spaceSlug, so "s//slug" is not a path.
const factiiiPath = (factiii: {
  spaceSlug?: string;
  slug: string;
}) => factiii.spaceSlug ? `s/${factiii.spaceSlug}/${factiii.slug}` : `f/${factiii.slug}`;
const PinnedFactiiiModal = props => {
  const $ = _c(46);
  const [query, setQuery] = useState("");
  const {
    reorder
  } = usePinnedFactiiis(client);
  const pinUserFactiii = usePinUserFactiii(client);
  let t0;
  if ($[0] !== props.canEdit || $[1] !== query) {
    t0 = props.canEdit && query.trim().length > 0;
    $[0] = props.canEdit;
    $[1] = query;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const searching = t0;
  let t1;
  let t2;
  if ($[3] !== query) {
    t1 = ["PinnedFactiiisModalSearch", query];
    t2 = () => unwrapFromResult(client.search.factiiis.query({
      query
    }));
    $[3] = query;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  const t3 = Boolean(searching);
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      placeholderData: keepPreviousData,
      staleTime: 30000
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data: results,
    isLoading: searchLoading
  } = useQuery(t4);
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      activationConstraint: {
        distance: 8
      }
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      activationConstraint: {
        delay: 200,
        tolerance: 5
      }
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      coordinateGetter: sortableKeyboardCoordinates
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const sensors = useSensors(useSensor(PointerSensor, t5), useSensor(TouchSensor, t6), useSensor(KeyboardSensor, t7));
  let t8;
  if ($[13] !== props.factiiis || $[14] !== reorder) {
    t8 = t9 => {
      const {
        active,
        over
      } = t9;
      if (!over || active.id === over.id) {
        return;
      }
      const ids = props.factiiis.map(_temp);
      reorder(arrayMove(ids, ids.indexOf(Number(active.id)), ids.indexOf(Number(over.id))));
    };
    $[13] = props.factiiis;
    $[14] = reorder;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleDragEnd = t8;
  let t9;
  if ($[16] !== props) {
    t9 = () => props.closeHandler();
    $[16] = props;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== props) {
    t10 = () => props.closeHandler();
    $[18] = props;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <h2>Pinned Factiiis</h2>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10) {
    t12 = <ModalHeader closeHandler={t10}>{t11}</ModalHeader>;
    $[21] = t10;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== props.canEdit || $[24] !== query || $[25] !== searchLoading) {
    t13 = props.canEdit && <Input className="w-full rounded" value={query} label="Search factiiis to pin..." onChange={setQuery} icons={[searchLoading ? <Loader2 key="loading" className="animate-spin" size={18} /> : <Search key="search" size={18} />]} />;
    $[23] = props.canEdit;
    $[24] = query;
    $[25] = searchLoading;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== handleDragEnd || $[28] !== pinUserFactiii || $[29] !== props || $[30] !== query || $[31] !== results || $[32] !== searchLoading || $[33] !== searching || $[34] !== sensors) {
    t14 = searching ? <div className="space-y-2">{searchLoading && !results && <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin" size={24} /></div>}{results?.factiiis.length === 0 && <p className="py-8 text-center text-sm text-textSecondary">No factiiis found matching "{query}"</p>}{results?.factiiis.map(factiii => <SearchRow key={factiii.id} factiii={factiii} pinned={props.factiiis.some(f_0 => f_0.id === factiii.id)} onToggle={() => void pinUserFactiii(factiii)} />)}</div> : <div className="space-y-2">{props.canEdit && <p className="text-sm text-textSecondary">{props.factiiis.length > 0 ? "Drag to reorder. Click a factiii to post with it." : "No pinned factiiis yet. Search above to pin one."}</p>}<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}><SortableContext items={props.factiiis.map(_temp2)} strategy={verticalListSortingStrategy}>{props.factiiis.map(factiii_0 => <PinRow key={factiii_0.id} factiii={factiii_0} canEdit={Boolean(props.canEdit)} onOpen={() => props.closeHandler(factiii_0)} onUnpin={() => void pinUserFactiii(factiii_0)} />)}</SortableContext></DndContext></div>;
    $[27] = handleDragEnd;
    $[28] = pinUserFactiii;
    $[29] = props;
    $[30] = query;
    $[31] = results;
    $[32] = searchLoading;
    $[33] = searching;
    $[34] = sensors;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14) {
    t15 = <div className="space-y-4">{t13}{t14}</div>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== props.canEdit || $[40] !== t15) {
    t16 = <ModalContent lockHeight={props.canEdit}>{t15}</ModalContent>;
    $[39] = props.canEdit;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t12 || $[43] !== t16 || $[44] !== t9) {
    t17 = <Modal closeHandler={t9}>{t12}{t16}</Modal>;
    $[42] = t12;
    $[43] = t16;
    $[44] = t9;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  return t17;
};
const PinRow = props => {
  const $ = _c(36);
  const t0 = !props.canEdit;
  let t1;
  if ($[0] !== props.factiii.id || $[1] !== t0) {
    t1 = {
      id: props.factiii.id,
      disabled: t0
    };
    $[0] = props.factiii.id;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t1);
  let t2;
  if ($[3] !== transform) {
    t2 = CSS.Transform.toString(transform);
    $[3] = transform;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = isDragging ? 0.6 : 1;
  let t4;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== transition) {
    t4 = {
      transform: t2,
      transition,
      opacity: t3
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = transition;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = props.onOpen;
  let t6;
  if ($[9] !== attributes || $[10] !== listeners || $[11] !== props.canEdit || $[12] !== props.factiii.name) {
    t6 = props.canEdit && <button type="button" aria-label={`Reorder ${props.factiii.name}`} className="cursor-grab touch-none rounded p-1 text-textSecondary hover:text-textPrimary active:cursor-grabbing" onClick={_temp3} {...attributes} {...listeners}><GripVertical size={18} /></button>;
    $[9] = attributes;
    $[10] = listeners;
    $[11] = props.canEdit;
    $[12] = props.factiii.name;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== props.factiii.avatarKey || $[15] !== props.factiii.id) {
    t7 = <Avatar factiiiAvatarKey={props.factiii.avatarKey} factiiiId={props.factiii.id} size={50} />;
    $[14] = props.factiii.avatarKey;
    $[15] = props.factiii.id;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== props.factiii.name) {
    t8 = <div className="truncate">{props.factiii.name}</div>;
    $[17] = props.factiii.name;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== props.factiii) {
    t9 = factiiiPath(props.factiii);
    $[19] = props.factiii;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <div className="truncate text-sm text-textSecondary">{t9}</div>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8) {
    t11 = <div className="min-w-0 flex-1">{t8}{t10}</div>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== props) {
    t12 = props.canEdit && <Button text="Unpin" variant="SECONDARY" borderTransparent={false} classNames="w-auto shrink-0 px-3 py-1 text-xs" onClick={event_0 => {
      event_0.stopPropagation();
      props.onUnpin();
    }} />;
    $[26] = props;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== props.onOpen || $[29] !== setNodeRef || $[30] !== t11 || $[31] !== t12 || $[32] !== t4 || $[33] !== t6 || $[34] !== t7) {
    t13 = <div ref={setNodeRef} style={t4} className="flex cursor-pointer items-center gap-2 rounded-md bg-background-secondary p-2 text-start hover:bg-background-light" onClick={t5}>{t6}{t7}{t11}{t12}</div>;
    $[28] = props.onOpen;
    $[29] = setNodeRef;
    $[30] = t11;
    $[31] = t12;
    $[32] = t4;
    $[33] = t6;
    $[34] = t7;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  return t13;
};
const SearchRow = props => {
  const $ = _c(20);
  let t0;
  if ($[0] !== props.factiii.avatarKey || $[1] !== props.factiii.id) {
    t0 = <Avatar factiiiAvatarKey={props.factiii.avatarKey} factiiiId={props.factiii.id} size={40} />;
    $[0] = props.factiii.avatarKey;
    $[1] = props.factiii.id;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== props.factiii.name) {
    t1 = <div className="truncate">{props.factiii.name}</div>;
    $[3] = props.factiii.name;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props.factiii) {
    t2 = factiiiPath(props.factiii);
    $[5] = props.factiii;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <div className="truncate text-sm text-textSecondary">{t2}</div>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t3) {
    t4 = <div className="min-w-0 flex-1">{t1}{t3}</div>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = props.pinned ? "Unpin" : "Pin";
  const t6 = props.pinned ? "SECONDARY" : "PRIMARY";
  let t7;
  if ($[12] !== props.onToggle || $[13] !== t5 || $[14] !== t6) {
    t7 = <Button text={t5} variant={t6} borderTransparent={false} classNames="w-auto shrink-0 px-3 py-1 text-xs" onClick={props.onToggle} />;
    $[12] = props.onToggle;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t0 || $[17] !== t4 || $[18] !== t7) {
    t8 = <div className="flex items-center gap-2 rounded-md border border-ds-border-standard p-2">{t0}{t4}{t7}</div>;
    $[16] = t0;
    $[17] = t4;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
export default PinnedFactiiiModal;
function _temp(f) {
  return f.id;
}
function _temp2(f_1) {
  return f_1.id;
}
function _temp3(event) {
  return event.stopPropagation();
}